import { EMPTY_PLATFORM_SETTINGS, PLATFORM_DEFAULTS, type PlatformSettings } from '@base/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.ts';
import { can, useMe } from '../../auth.tsx';
import { ImageField, readPlatformImage } from '../../components/ImageField.tsx';
import { useLeaveGuard } from '../../components/LeaveGuard.tsx';
import { SISTEMA_NOME } from '../../sistema.ts';
import {
  Badge,
  Button,
  ErrorBox,
  Field,
  Input,
  issueFor,
  Loading,
  PageHeader,
  Panel,
} from '../../components/ui.tsx';
import {
  applyPlatformColors,
  DEFAULT_LOGO_URL,
  DEFAULT_SYMBOL_URL,
  PLATFORM_KEY,
  usePlatformSettings,
} from '../../platform.tsx';

type Form = Omit<PlatformSettings, 'updatedAt'>;

const COLORS = [
  { key: 'accentColor', fallback: PLATFORM_DEFAULTS.accentColor },
  { key: 'paperColor', fallback: PLATFORM_DEFAULTS.paperColor },
  { key: 'inkColor', fallback: PLATFORM_DEFAULTS.inkColor },
] as const;

const IMAGES = ['logo', 'symbol'] as const;

/**
 * Configuração › Plataforma: nome, logo, símbolo e cores do sistema, para todo mundo. As cores
 * mudam a tela na hora (prévia); só valem para os outros depois de salvar. A fonte é fixa (IBM
 * Plex Sans).
 */
export function PlatformPage() {
  const { t } = useTranslation();
  const settings = usePlatformSettings();
  return (
    <>
      <PageHeader
        title={t('nav.platform')}
        description={t('platform.description')}
        meta={
          settings.data &&
          (settings.data.updatedAt ? (
            <Badge tone="ok">{t('platform.customized')}</Badge>
          ) : (
            <Badge tone="info">{t('platform.usingDefault')}</Badge>
          ))
        }
      />
      {settings.isPending ? (
        <Loading />
      ) : settings.isError ? (
        <ErrorBox error={settings.error} />
      ) : (
        <PlatformForm initial={settings.data} />
      )}
    </>
  );
}

function toForm(s: PlatformSettings): Form {
  const { updatedAt: _ignored, ...rest } = s;
  return rest;
}

function PlatformForm({ initial }: { initial: PlatformSettings }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { data: me } = useMe();
  const readOnly = !can(me, 'platform.settings.manage');
  const start = toForm(initial);
  const [form, setForm] = useState<Form>(start);
  const [imageError, setImageError] = useState<string>();
  const dirty = JSON.stringify(form) !== JSON.stringify(start);
  const leave = useLeaveGuard(dirty && !readOnly);

  // Prévia: a tela inteira acompanha as cores escolhidas; ao sair sem salvar, volta o que está gravado.
  useEffect(() => {
    applyPlatformColors(form);
  }, [form]);
  useEffect(
    () => () => applyPlatformColors(client.getQueryData<PlatformSettings>(PLATFORM_KEY) ?? initial),
    [client, initial],
  );

  const save = useMutation({
    mutationFn: () => api.put<PlatformSettings>('/platform/settings', form),
    onSuccess: (data) => {
      leave.allowLeave();
      client.setQueryData(PLATFORM_KEY, data);
      setForm(toForm(data));
    },
  });

  async function pick(key: (typeof IMAGES)[number], file: File | undefined) {
    setImageError(undefined);
    if (!file) return;
    const read = await readPlatformImage(file);
    if ('error' in read) setImageError(t(`platform.${read.error}`));
    else setForm((f) => ({ ...f, [key]: read.url }));
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <ErrorBox error={save.error} showPaths={false} />
      {readOnly && <p className="text-sm text-muted">{t('platform.readOnly')}</p>}
      <Panel title={t('platform.identity')}>
        <Field
          label={t('platform.appName')}
          hint={t('platform.appNameHint')}
          error={issueFor(save.error, 'appName')}
        >
          <Input
            value={form.appName ?? ''}
            maxLength={40}
            placeholder={SISTEMA_NOME}
            disabled={readOnly}
            onChange={(e) => setForm({ ...form, appName: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          {IMAGES.map((key) => (
            <ImageField
              key={key}
              label={t(`platform.${key}`)}
              hint={t(`platform.${key}Hint`)}
              fallback={key === 'logo' ? DEFAULT_LOGO_URL : DEFAULT_SYMBOL_URL}
              value={form[key]}
              readOnly={readOnly}
              onPick={(file) => void pick(key, file)}
              onReset={() => setForm({ ...form, [key]: null })}
            />
          ))}
        </div>
        {imageError && <p className="text-sm text-danger">{imageError}</p>}
      </Panel>
      <Panel title={t('platform.colors')}>
        <p className="text-sm text-muted">{t('platform.colorsHint')}</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {COLORS.map(({ key, fallback }) => {
            const value = form[key] ?? fallback;
            return (
              <Field key={key} label={t(`platform.${key}`)} hint={t(`platform.${key}Hint`)}>
                <span className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label={t(`platform.${key}`)}
                    disabled={readOnly}
                    value={value.toLowerCase()}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value.toUpperCase() })}
                    className="h-10 w-12 cursor-pointer rounded border border-control-border bg-sheet"
                  />
                  <span className="font-mono text-xs">{value.toUpperCase()}</span>
                  {form[key] && !readOnly && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, [key]: null })}
                      title={t('platform.useDefault')}
                      aria-label={`${t('platform.useDefault')}: ${t(`platform.${key}`)}`}
                      className="ml-auto rounded-md p-1.5 text-muted hover:text-ink"
                    >
                      <RotateCcw size={14} aria-hidden />
                    </button>
                  )}
                </span>
              </Field>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-rule bg-paper p-4 text-sm">
          <span className="text-muted">{t('platform.sample')}</span>
          <Button>{t('platform.sampleButton')}</Button>
          <span className="rounded-md bg-brand-soft px-2.5 py-1.5 font-medium text-brand-text">
            {t('platform.sampleActive')}
          </span>
          <a href="#" onClick={(e) => e.preventDefault()} className="text-brand-text underline">
            {t('platform.sampleLink')}
          </a>
        </div>
      </Panel>
      <Panel title={t('platform.font')}>
        <p className="text-sm text-muted">{t('platform.fontHint')}</p>
      </Panel>
      {!readOnly && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            variant="secondary"
            disabled={
              save.isPending ||
              JSON.stringify(form) === JSON.stringify(toForm(EMPTY_PLATFORM_SETTINGS))
            }
            onClick={() => setForm(toForm(EMPTY_PLATFORM_SETTINGS))}
          >
            {t('platform.resetAll')}
          </Button>
          <Button disabled={save.isPending || !dirty} onClick={() => save.mutate()}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      )}
      {leave.dialog}
    </div>
  );
}
